import type { TranscriptSegment } from '@ai-meeting/shared'
import type { LiveTranscriptTurn } from '@/utils/liveTranscriptTurns'
import { resolveTimelineBaselineMs } from './transcriptPreviewWindow'

/**
 * Where a saved line sits on the meeting's clock, in milliseconds from zero.
 *
 * `elapsedMs` is the offset the room's own clock gave the line, so it is the
 * same number on every machine that was in the room and matches the labels
 * the live screen showed while it was being said (BRAI-539). Deriving a zero
 * from the lines in hand is the fallback for meetings saved before that field
 * existed, and only that: each machine in a shared room holds a different
 * earliest line, so the same sentence comes out labelled differently on each,
 * and a line that arrived before someone joined reads as 00:00.
 *
 * The end keeps the line's own span rather than being measured from the
 * baseline again, so both paths agree on how long a line lasted.
 */
function offsetsOf(segment: TranscriptSegment, baselineMs: number) {
  const startMs = segment.elapsedMs ?? segment.timestamp - baselineMs
  const spanMs = segment.endTimestamp === undefined ? 0 : segment.endTimestamp - segment.timestamp
  return { startMs, endMs: startMs + spanMs }
}

/**
 * Whether these segments carry a clock at all.
 *
 * An internal meeting transcribes nothing live: its transcript is parsed back
 * out of the flat text the stored-audio run returns, and that payload has no
 * timings to parse. `AudioMeetingMinutesResponse` is the structured minutes,
 * the transcript as one string — "mỗi dòng một câu, mở đầu bằng nhãn
 * diarization" — and the recording's total length. Nothing per line.
 *
 * So the parser stamps every line zero, and the page printed `00:00` on every
 * row: not "unknown" but "all of this was said at the start", which is a
 * claim nobody made (BRAI-735). Showing no time is the honest answer; real
 * per-line timings would have to come from the AI service first.
 *
 * `timestamp` is wall clock wherever it is real, so an exact zero on every
 * line of both arrays is the signature of a transcript that never had one.
 *
 * @param groups The source and translation arrays, together.
 */
function hasTimings(groups: TranscriptSegment[][]): boolean {
  return groups.some((segments) =>
    segments.some((segment) => segment.elapsedMs !== undefined || segment.timestamp !== 0)
  )
}

/** A translation can only follow the line it translates, never precede it. */
function byTimestamp(a: TranscriptSegment, b: TranscriptSegment): number {
  return a.timestamp - b.timestamp
}

/** The speaker a row is filed under, matching how the saved segments name them. */
function speakerOf(segment: TranscriptSegment): string {
  return segment.speakerId || segment.speaker || '1'
}

/**
 * Pair a saved meeting's two segment arrays into the bilingual turns the
 * Enhanced-mode timeline renders (BRAI-642).
 *
 * A meeting is stored as two independent arrays — `transcript` and
 * `translationSegments` — with **no key linking them**. The two-column view
 * this replaces never had to relate them: it drew each array in its own
 * column and let the reader's eye do the matching. A timeline has to put the
 * two on the same row, so the link has to be reconstructed.
 *
 * The rule is the one thing both arrays genuinely share: a clock. Every
 * translation is attached to the latest source line that had already started
 * when the translation was stamped, because a translation cannot precede its
 * source. That is exact rather than approximate for the enhanced path —
 * `buildTranslationSegmentsFromTurns` stamps each translation with its turn's
 * start, which *is* the start of the source run it covers — and is a close
 * approximation for the older inline path, where a translation token is
 * stamped a moment after the words it renders.
 *
 * Several translations landing on one source line are joined in arrival
 * order, which is what happens when one spoken run is translated in pieces.
 *
 * The other direction is the one the saved table was getting wrong. Enhanced
 * mode shows one output for a whole spoken run, but the source is stored as
 * the separate sentences STT emitted, so each sentence became its own row and
 * only the first carried the output. A translation stamped at its line's
 * start — which is how `buildTranslationSegmentsFromTurns` marks the
 * start of the run — claims the following sentences of that same speaker,
 * up to the next line that has an output of its own. Their text is joined
 * into the one input beside that output. Another speaker's line ends the
 * run: sentences this speaker says afterwards stay later in the timeline,
 * so one output cannot stretch its clock across the rest of the meeting.
 * A translation stamped after its line is the older inline path and stays
 * on that line alone.
 *
 * @param segments            The meeting's `transcript`, in any order.
 * @param translationSegments The meeting's `translationSegments`, in any order.
 * @returns One turn per output run, oldest first. A sentence no output
 *   claims stays its own row. Offsets come from {@link offsetsOf} so the
 *   elapsed labels read the same as the live screen's did. A meeting with
 *   translations but no source lines is a broken recording rather than an
 *   empty one, so those translations still get rows of their own instead of
 *   being dropped.
 */
export function pairStoredTranscriptTurns(
  segments: TranscriptSegment[],
  translationSegments: TranscriptSegment[]
): LiveTranscriptTurn[] {
  const sources = segments.filter((segment) => segment.text?.trim()).sort(byTimestamp)
  const translations = translationSegments
    .filter((segment) => segment.text?.trim())
    .sort(byTimestamp)

  // Nothing to hang the translations on. Showing them unattached beats showing
  // an empty card over a meeting that plainly recorded something.
  // One baseline across both arrays, not one per array: the fallback path
  // renders the two on the same rows, and a zero derived per column would put
  // them on different clocks.
  const baselineMs = resolveTimelineBaselineMs([sources, translations])
  // Absent beats wrong: see {@link hasTimings}.
  const timed = hasTimings([sources, translations])

  if (sources.length === 0) {
    return translations.map((segment, index) => ({
      id: segment.id ?? `stored-translation-${index}`,
      sourceSegmentIds: [],
      speaker: speakerOf(segment),
      ...(segment.speakerName ? { speakerName: segment.speakerName } : {}),
      batchTranslation: segment.text,
      ...(timed ? offsetsOf(segment, baselineMs) : {}),
      isDraft: false,
      speakerBlockId: `block-${index}-${speakerOf(segment)}`,
    }))
  }
  /** Translations that landed on one source line, in arrival order. */
  const translationsFor = new Map<number, string[]>()
  /**
   * Owners whose translation was stamped at the line's own start. Several
   * outputs sharing that instant are a batch that could not be ordered by
   * time — they stay on the one line, and do not swallow the sentences after
   * it. A single output stamped there is the enhanced-mode mark that the
   * line opens a source run.
   */
  const stampedAtStart = new Set<number>()

  for (const translation of translations) {
    // The last source that had already started. `findLastIndex` would read
    // better but is ES2023; this file is built for the browser baseline.
    let owner = 0
    for (let index = 0; index < sources.length; index += 1) {
      if (sources[index].timestamp <= translation.timestamp) owner = index
      else break
    }
    const held = translationsFor.get(owner)
    if (held) held.push(translation.text)
    else translationsFor.set(owner, [translation.text])
    if (translation.timestamp <= sources[owner].timestamp) stampedAtStart.add(owner)
  }

  const opensRun = new Set(
    [...stampedAtStart].filter((index) => translationsFor.get(index)?.length === 1)
  )

  let blockId = ''
  let previousSpeaker: string | null = null
  const consumed = new Set<number>()
  const turns: LiveTranscriptTurn[] = []

  for (let index = 0; index < sources.length; index += 1) {
    if (consumed.has(index)) continue

    const group = [sources[index]]
    // Pull in the later sentences this output covers, while the same person
    // is still speaking. The next output starts the following run. Stopping
    // at a speaker change keeps the rows in time order: a line said at 01:30
    // must not sit under a block whose clock already runs to 07:49.
    if (opensRun.has(index)) {
      const speaker = speakerOf(sources[index])
      for (let next = index + 1; next < sources.length; next += 1) {
        if (translationsFor.has(next) || speakerOf(sources[next]) !== speaker) break
        group.push(sources[next])
        consumed.add(next)
      }
    }

    const segment = group[0]
    const speaker = speakerOf(segment)
    // A run of consecutive lines from one speaker is one block, which is what
    // the timeline draws a single speaker heading over.
    if (speaker !== previousSpeaker) {
      blockId = `block-${index}-${speaker}`
      previousSpeaker = speaker
    }
    const translation = translationsFor.get(index)?.join(' ')
    const start = offsetsOf(segment, baselineMs)
    const last = offsetsOf(group[group.length - 1], baselineMs)
    turns.push({
      id: segment.id ?? `stored-${index}`,
      sourceSegmentIds: group.flatMap((part) => (part.id ? [part.id] : [])),
      speaker,
      ...(segment.speakerName ? { speakerName: segment.speakerName } : {}),
      sourceText:
        group.length === 1 ? segment.text : group.map((part) => part.text.trim()).join(' '),
      ...(translation ? { batchTranslation: translation } : {}),
      ...(timed
        ? {
            startMs: start.startMs,
            endMs: group.length === 1 ? start.endMs : Math.max(start.endMs, last.endMs),
          }
        : {}),
      isDraft: false,
      speakerBlockId: blockId,
    })
  }

  return turns
}
